/**
 * Chart Parser
 *
 * Loads and validates chart JSON files
 */

import { NoteData } from '../gameplay/Note';

export interface ChartMetadata {
  title: string;
  artist: string;
  charter?: string;
  difficulty?: string;
  bpm: number;
  offset: number;
  previewStart?: number;
}

export interface ChartData {
  metadata: ChartMetadata;
  notes: NoteData[];
}

export class ChartParser {
  /**
   * Load a chart from a URL
   */
  async loadChart(url: string): Promise<ChartData> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load chart: ${response.statusText}`);
      }

      const data = await response.json();
      return this.validate(data);
    } catch (error) {
      console.error('Error loading chart:', error);
      throw error;
    }
  }

  /**
   * Validate and normalize chart data
   */
  private validate(data: any): ChartData {
    if (!data.metadata) {
      throw new Error('Chart missing metadata');
    }

    if (!data.notes || !Array.isArray(data.notes)) {
      throw new Error('Chart missing notes array');
    }

    // Validate metadata
    const metadata: ChartMetadata = {
      title: data.metadata.title || 'Unknown',
      artist: data.metadata.artist || 'Unknown',
      charter: data.metadata.charter,
      difficulty: data.metadata.difficulty || 'Normal',
      bpm: data.metadata.bpm || 120,
      offset: data.metadata.offset || 0,
      previewStart: data.metadata.previewStart || 0
    };

    // Validate and sort notes
    const notes: NoteData[] = data.notes
      .filter((note: any) => {
        // Basic validation
        return (
          typeof note.time === 'number' &&
          typeof note.lane === 'number' &&
          note.lane >= 0 &&
          note.lane < 4 &&
          typeof note.type === 'string'
        );
      })
      .map((note: any) => ({
        time: note.time,
        lane: note.lane,
        type: note.type
      }))
      .sort((a: NoteData, b: NoteData) => a.time - b.time);

    console.log(`Chart loaded: ${metadata.title} by ${metadata.artist} (${notes.length} notes)`);

    return { metadata, notes };
  }

  /**
   * Create a simple test chart programmatically
   */
  static createTestChart(): ChartData {
    return {
      metadata: {
        title: 'Test Chart',
        artist: 'BeatShift',
        bpm: 120,
        offset: 0
      },
      notes: [
        { time: 2000, lane: 0, type: 'tap' },
        { time: 2500, lane: 1, type: 'tap' },
        { time: 3000, lane: 2, type: 'tap' },
        { time: 3500, lane: 3, type: 'tap' },
        { time: 4000, lane: 1, type: 'tap' },
        { time: 4000, lane: 2, type: 'tap' },
        { time: 4500, lane: 0, type: 'tap' },
        { time: 4500, lane: 3, type: 'tap' },
        { time: 5000, lane: 0, type: 'tap' },
        { time: 5000, lane: 3, type: 'tap' },
        { time: 5250, lane: 1, type: 'tap' },
        { time: 5250, lane: 2, type: 'tap' }
      ]
    };
  }
}
